import React, { useState } from "react";
import {
  ArrowRight,
  Check,
  Clock3,
  Info,
  LockKeyhole,
  MapPin,
  Navigation,
  PackageCheck,
  Sparkles,
  Truck,
  X,
} from "lucide-react";

import PublicNavbar from "../components/PublicNavbar";
import { checkPublicDeliveryAddress } from "../../delivery/deliveryPublicApi";
import "./delivery-page.css";


function money(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "";
}

function addressLabel(address, result) {
  if (result?.formatted_address) return result.formatted_address;
  const cityLine = [address.city, address.state, address.zip].filter(Boolean).join(" ");
  return cityLine || "Delivery address";
}

function requiredAddressMissing(address) {
  return !address.street.trim() || !address.city.trim() || !address.zip.trim();
}

const deliveryZones = [
  {
    id: "free",
    icon: Check,
    title: "Free Delivery Zone",
    description:
      "Qualifying orders within our standard delivery area receive free delivery.",
    note: "Based on delivery address",
  },
  {
    id: "paid",
    icon: Truck,
    title: "Extended Delivery Area",
    description:
      "Some addresses outside the free zone may still be eligible for delivery with an added fee.",
    note: "Fee shown after address check",
  },
  {
    id: "unavailable",
    icon: X,
    title: "Outside Service Area",
    description:
      "If an address is too far from our delivery area, delivery may not be available.",
    note: "Availability depends on location",
  },
];

export default function DeliveryPage() {
  const [address, setAddress] = useState({
    street: "",
    apartment: "",
    city: "",
    state: "MD",
    zip: "",
  });

  const [deliveryStatus, setDeliveryStatus] = useState("unchecked");
  const [deliveryResult, setDeliveryResult] = useState(null);
  const [deliveryError, setDeliveryError] = useState("");
  const [isCheckingDelivery, setIsCheckingDelivery] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setAddress((current) => ({
      ...current,
      [name]: value,
    }));
    setDeliveryStatus("unchecked");
    setDeliveryResult(null);
    setDeliveryError("");
  };

  const checkDelivery = async () => {
    if (requiredAddressMissing(address)) {
      setDeliveryStatus("missing");
      setDeliveryResult(null);
      setDeliveryError("");
      return;
    }

    setIsCheckingDelivery(true);
    setDeliveryError("");

    try {
      const result = await checkPublicDeliveryAddress(address);
      setDeliveryResult(result);
      setDeliveryStatus(result.status || "unavailable");
    } catch (requestError) {
      setDeliveryResult(null);
      setDeliveryStatus("error");
      setDeliveryError(requestError.message || "Delivery availability could not be checked.");
    } finally {
      setIsCheckingDelivery(false);
    }
  };

  return (
    <div className="sl-delivery-page">
      <PublicNavbar />

      <main>
        <section className="sl-delivery-hero">
          <div className="sl-delivery-shell sl-delivery-hero-grid">
            <div className="sl-delivery-hero-copy">
              <span className="sl-delivery-eyebrow">DELIVERY INFORMATION</span>

              <h1>
                Sweet treats,
                <span> delivered to you.</span>
              </h1>

              <p>
                SugaLumps is delivery only. Enter your address below to see
                whether your order qualifies for free delivery, requires a
                delivery fee, or falls outside our current service area.
              </p>

              <div className="sl-delivery-hero-facts">
                <div>
                  <Truck size={18} strokeWidth={1.6} />
                  <span>
                    <strong>Delivery only</strong>
                    <small>No pickup available</small>
                  </span>
                </div>

                <div>
                  <MapPin size={18} strokeWidth={1.6} />
                  <span>
                    <strong>Serving the DMV</strong>
                    <small>Silver Spring &amp; surrounding areas</small>
                  </span>
                </div>

                <div>
                  <Clock3 size={18} strokeWidth={1.6} />
                  <span>
                    <strong>Made to order</strong>
                    <small>Please allow preparation time</small>
                  </span>
                </div>
              </div>
            </div>

            <div className="sl-delivery-hero-card">
              <div className="sl-delivery-hero-card-icon">
                <Truck size={33} strokeWidth={1.45} />
              </div>

              <span className="sl-delivery-hero-card-kicker">
                HOW DELIVERY WORKS
              </span>

              <h2>Your address determines your delivery options.</h2>

              <p>
                We calculate eligibility using the delivery address connected
                to your order. Final availability and any delivery charge are
                confirmed before checkout.
              </p>

              <div className="sl-delivery-hero-card-steps">
                <span>1</span>
                <p>Enter your delivery address.</p>

                <span>2</span>
                <p>We check whether the location is serviceable.</p>

                <span>3</span>
                <p>See whether delivery is free, paid, or unavailable.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="sl-delivery-check-section">
          <div className="sl-delivery-shell">
            <div className="sl-delivery-section-heading">
              <div>
                <span className="sl-delivery-eyebrow">CHECK YOUR ADDRESS</span>
                <h2>Can we deliver to you?</h2>
              </div>

              <p>
                Enter the address where you want your order delivered. This
                to see whether delivery is free, available for a fee, or outside our service area.
              </p>
            </div>

            <div className="sl-delivery-check-card">
              <div className="sl-delivery-address-form">
                <div className="sl-delivery-form-heading">
                  <span>
                    <Navigation size={19} strokeWidth={1.6} />
                  </span>

                  <div>
                    <h3>Delivery Address</h3>
                    <p>
                      We’ll use this to determine availability and delivery
                      cost.
                    </p>
                  </div>
                </div>

                <div className="sl-delivery-form-row sl-delivery-form-row-top">
                  <label>
                    <span>Street Address</span>
                    <input
                      type="text"
                      name="street"
                      value={address.street}
                      onChange={handleChange}
                      placeholder="123 Main Street"
                    />
                  </label>

                  <label>
                    <span>Apt / Suite (optional)</span>
                    <input
                      type="text"
                      name="apartment"
                      value={address.apartment}
                      onChange={handleChange}
                      placeholder="Apt 4B"
                    />
                  </label>
                </div>

                <div className="sl-delivery-form-row sl-delivery-form-row-bottom">
                  <label>
                    <span>City</span>
                    <input
                      type="text"
                      name="city"
                      value={address.city}
                      onChange={handleChange}
                      placeholder="Silver Spring"
                    />
                  </label>

                  <label>
                    <span>State</span>
                    <select
                      name="state"
                      value={address.state}
                      onChange={handleChange}
                    >
                      <option value="MD">MD</option>
                      <option value="DC">DC</option>
                      <option value="VA">VA</option>
                    </select>
                  </label>

                  <label>
                    <span>ZIP Code</span>
                    <input
                      type="text"
                      name="zip"
                      value={address.zip}
                      onChange={handleChange}
                      placeholder="20910"
                    />
                  </label>
                </div>

                <button
                  type="button"
                  className="sl-delivery-check-button"
                  onClick={checkDelivery}
                  disabled={isCheckingDelivery}
                >
                  <MapPin size={17} strokeWidth={1.8} />
                  {isCheckingDelivery ? "Checking delivery..." : "Check Delivery Availability"}
                  <ArrowRight size={16} strokeWidth={1.8} />
                </button>

                <div className="sl-delivery-privacy">
                  <LockKeyhole size={13} strokeWidth={1.7} />
                  <span>
                    Your address is only used to check delivery availability.
                  </span>
                </div>
              </div>

              <div className="sl-delivery-result-panel">
                {deliveryStatus === "unchecked" && (
                  <div className="sl-delivery-result-empty">
                    <span>
                      <MapPin size={33} strokeWidth={1.35} />
                    </span>

                    <h3>Enter your address to get started.</h3>

                    <p>
                      We’ll show whether delivery is free, available for a fee,
                      or currently outside our service area.
                    </p>
                  </div>
                )}

                {deliveryStatus === "missing" && (
                  <div className="sl-delivery-result-message is-error">
                    <span>
                      <Info size={24} strokeWidth={1.7} />
                    </span>

                    <div>
                      <h3>We need a little more information.</h3>
                      <p>
                        Enter a street address, city, and ZIP code before
                        checking delivery availability.
                      </p>
                    </div>
                  </div>
                )}

                {deliveryStatus === "free" && (
                  <div className="sl-delivery-result-message is-success">
                    <span>
                      <Check size={25} strokeWidth={2} />
                    </span>

                    <div>
                      <small>{addressLabel(address, deliveryResult)}</small>
                      <h3>Free delivery</h3>
                      <strong>Free</strong>
                      <p>
                        You're within our {deliveryResult?.free_delivery_minutes ?? "configured"}-minute delivery area.
                      </p>

                      <div className="sl-delivery-result-meta">
                        <span>
                          <Clock3 size={14} strokeWidth={1.7} />
                          Approximately {deliveryResult?.duration_minutes} minutes away
                        </span>

                        <span>
                          <PackageCheck size={14} strokeWidth={1.7} />
                          Based on delivery address
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {deliveryStatus === "paid" && (
                  <div className="sl-delivery-result-message is-paid">
                    <span>
                      <Truck size={25} strokeWidth={1.8} />
                    </span>

                    <div>
                      <small>{addressLabel(address, deliveryResult)}</small>
                      <h3>Delivery available</h3>
                      <strong>{money(deliveryResult?.delivery_fee)} delivery fee</strong>
                      <p>
                        This address is within our extended delivery area.
                      </p>

                      <div className="sl-delivery-result-meta">
                        <span>
                          <Clock3 size={14} strokeWidth={1.7} />
                          Approximately {deliveryResult?.duration_minutes} minutes away
                        </span>

                        <span>
                          <PackageCheck size={14} strokeWidth={1.7} />
                          Fee from delivery settings
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {deliveryStatus === "unavailable" && (
                  <div className="sl-delivery-result-message is-error">
                    <span>
                      <X size={24} strokeWidth={1.8} />
                    </span>

                    <div>
                      <small>{addressLabel(address, deliveryResult)}</small>
                      <h3>Outside service area</h3>
                      <p>
                        {deliveryResult?.message || "We're unable to deliver to this address."}
                      </p>
                    </div>
                  </div>
                )}

                {deliveryStatus === "error" && (
                  <div className="sl-delivery-result-message is-error">
                    <span>
                      <Info size={24} strokeWidth={1.7} />
                    </span>

                    <div>
                      <h3>Delivery check unavailable</h3>
                      <p>{deliveryError || "Delivery availability could not be checked."}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="sl-delivery-zones-section">
          <div className="sl-delivery-shell">
            <div className="sl-delivery-section-heading">
              <div>
                <span className="sl-delivery-eyebrow">
                  DELIVERY AVAILABILITY
                </span>
                <h2>Three possible delivery results.</h2>
              </div>

              <p>
                Your exact result depends on the address entered, not just your
                city or ZIP code.
              </p>
            </div>

            <div className="sl-delivery-zone-grid">
              {deliveryZones.map(({ id, icon: Icon, title, description, note }) => (
                <article className={`sl-delivery-zone-card ${id}`} key={id}>
                  <span className="sl-delivery-zone-icon">
                    <Icon size={24} strokeWidth={1.55} />
                  </span>

                  <h3>{title}</h3>
                  <p>{description}</p>
                  <small>{note}</small>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="sl-delivery-info-section">
          <div className="sl-delivery-shell sl-delivery-info-grid">
            <div className="sl-delivery-info-copy">
              <span className="sl-delivery-eyebrow">BEFORE YOU ORDER</span>
              <h2>What to know about SugaLumps delivery.</h2>

              <div className="sl-delivery-info-list">
                <div>
                  <span>
                    <Truck size={19} strokeWidth={1.6} />
                  </span>

                  <div>
                    <h3>Delivery only</h3>
                    <p>
                      SugaLumps currently fulfills orders through delivery
                      rather than customer pickup.
                    </p>
                  </div>
                </div>

                <div>
                  <span>
                    <MapPin size={19} strokeWidth={1.6} />
                  </span>

                  <div>
                    <h3>Address-based pricing</h3>
                    <p>
                      Delivery cost is determined by the destination. Some
                      addresses qualify for free delivery while others may
                      include an additional fee.
                    </p>
                  </div>
                </div>

                <div>
                  <span>
                    <Clock3 size={19} strokeWidth={1.6} />
                  </span>

                  <div>
                    <h3>Plan ahead</h3>
                    <p>
                      Desserts are made to order, so preparation time is part of
                      the ordering process. Larger and custom orders may require
                      additional notice.
                    </p>
                  </div>
                </div>

                <div>
                  <span>
                    <Sparkles size={19} strokeWidth={1.6} />
                  </span>

                  <div>
                    <h3>Final details before checkout</h3>
                    <p>
                      Your delivery eligibility, delivery cost, and order total
                      should all be shown before the customer completes payment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <aside className="sl-delivery-summary-card">
              <span className="sl-delivery-summary-icon">
                <PackageCheck size={29} strokeWidth={1.5} />
              </span>

              <span className="sl-delivery-eyebrow">QUICK SUMMARY</span>
              <h3>Delivery at a glance</h3>

              <dl>
                <div>
                  <dt>Fulfillment</dt>
                  <dd>Delivery only</dd>
                </div>

                <div>
                  <dt>Primary area</dt>
                  <dd>Silver Spring &amp; DMV</dd>
                </div>

                <div>
                  <dt>Delivery price</dt>
                  <dd>Based on address</dd>
                </div>

                <div>
                  <dt>Free delivery</dt>
                  <dd>Available for qualifying addresses</dd>
                </div>

              </dl>

              <a href="/menu" className="sl-delivery-summary-button">
                Browse the Menu
                <ArrowRight size={15} strokeWidth={1.8} />
              </a>
            </aside>
          </div>
        </section>

        <section className="sl-delivery-cta-section">
          <div className="sl-delivery-shell">
            <div className="sl-delivery-cta-card">
              <div>
                <span className="sl-delivery-eyebrow">READY TO ORDER?</span>
                <h2>Build your box of something sweet.</h2>
                <p>
                  Browse cupcakes, pie pockets, pies, cheesecakes, and more.
                </p>
              </div>

              <a href="/menu" className="sl-delivery-cta-button">
                View the Menu
                <ArrowRight size={16} strokeWidth={1.8} />
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}